<template>
  <div class="container">
    <!-- Show Loading Spinner -->
    <div v-if="loading1" class="loading-container">
      <i class="fa fa-spinner fa-spin"></i>
      <span>Loading...</span>
    </div>

    <div v-if="!loading1 && totalPromotionsCount === 0" class="promo-section not-found-widget">
      <img src="../../../../assets/vectors/data_not_found.png" alt="No Promotions" class="error-image" />
      <p class="text-center error-message">There are no any active promotions.</p>
    </div>

    <div v-if="!loading1 && totalPromotionsCount !== 0" class="promo-section">
      <!-- Header -->
      <h5>Manage Promotion Priority</h5>

      <!-- Prioritized Promotions -->
      <div v-if="prioritizedPromotions.length > 0" class="priority-group">
        <h6 class="group-title">Prioritized Promotions</h6>
        <p class="group-desc">Drag and drop promotions to reorder their priority. The first item will have the highest priority.</p>

        <!-- Promotions List with Drag & Drop -->
        <draggable v-model="prioritizedPromotions" handle=".drag-handle" animation="200" item-key="id">
          <template v-slot:item="{ element, index }">
            <div class="promo-card">
              <div class="priority-order">
                <span>{{ index + 1 }}</span>
              </div>
              <div class="promo-info">
                <div class="promo-code"><strong>Promo Code: {{ element.code }}</strong></div>
                <div class="promo-details">{{ element.name }} ({{ element.startDate }} - {{ element.endDate }})</div>
              </div>
              <span class="drag-handle">
            <i class="fa-solid fa-grip-vertical"></i>
          </span>
            </div>
          </template>
        </draggable>
      </div>

      <!-- Non-Prioritized Promotions -->
      <div v-if="nonPrioritizedPromotions.length > 0" class="priority-group" style="margin-top: 30px;">
        <h6 class="group-title">Non-Prioritized Promotions</h6>
        <p class="group-desc">These promotions do not have a configurable priority.</p>

        <div v-for="element in nonPrioritizedPromotions" :key="element.id" class="promo-card non-draggable">
          <div class="promo-info" style="padding-left: 10px;">
            <div class="promo-code"><strong>Promo Code: {{ element.code }}</strong></div>
            <div class="promo-details">{{ element.name }} ({{ element.startDate }} - {{ element.endDate }})</div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions" style="margin-top: 20px;">
        <button @click="resetForm()">Cancel</button>
        <button @click="changePriority()" :disabled="loading2 || prioritizedPromotions.length === 0">
            <span v-if="loading2">
              <span class="spinner"></span> Processing...
            </span>
          <span v-else>
              Change Priority
            </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchAllPromotions, promotionPriority } from "@/services/dataService";
import { useToast } from "vue-toastification";
import draggable from "vuedraggable";

export default {
  components: { draggable },

  data() {
    return {
      token: localStorage.getItem('token_set'), //Get the API access token from the local storage
      profileId: localStorage.getItem('profile_id'), //Get the Logged user's profileId from local storage
      reportingProfileId: localStorage.getItem("reportingL1ProfileId"),
      userCategory: localStorage.getItem("user_label"),
      loading1: false,
      loading2: false,
      prioritizedPromotions: [],
      nonPrioritizedPromotions: [],
      priorityOrder: [],
    }
  },

  computed: {
    totalPromotionsCount() {
      return this.prioritizedPromotions.length + this.nonPrioritizedPromotions.length;
    }
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  mounted() {
    //Call fetch promotions list API
    this.fetchPromotionsList();
  },

  methods: {
    //Fetch promotions list by calling API
    async fetchPromotionsList() {
      try {
        //Set Loading Status
        this.loading1 = true;

        //Reset Lists before fetch data
        this.prioritizedPromotions = [];
        this.nonPrioritizedPromotions = [];

        let idToUse = this.profileId;
        if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
          idToUse = this.reportingProfileId;
        }

        //Call API to get promotions list
        const response = await fetchAllPromotions(this.token, idToUse);
        if (response.data.status === "success") {
          const activePromotions = response.data.data.filter(promotion => promotion.status === "1"); // ✅ only active promotions
          
          this.prioritizedPromotions = activePromotions
              .filter(p => ["Free Issue", "Discount"].includes(p.promotion_type))
              .map(promotion => ({
                id: promotion.id,
                code: promotion.promotion_code,
                name: promotion.promotion_name,
                startDate: promotion.start_date,
                endDate: promotion.end_date,
                type: promotion.promotion_type
              }));

          this.nonPrioritizedPromotions = activePromotions
              .filter(p => ["Competitor Price Discount", "Trust"].includes(p.promotion_type))
              .map(promotion => ({
                id: promotion.id,
                code: promotion.promotion_code,
                name: promotion.promotion_name,
                startDate: promotion.start_date,
                endDate: promotion.end_date,
                type: promotion.promotion_type
              }));
        } else {
          this.toast.error(response.data.message);
        }
      } catch (e) {
        this.toast.error("Failed to fetch promotions list.");
      } finally {
        this.loading1 = false;
      }
    },

    //Call API to change Promotion Priority
    async changePriority() {
      try {
        //Set Loading Status
        this.loading2 = true;

        //Map Promotion Priority Order (convert to int) for Prioritized Promotions only
        this.priorityOrder = this.prioritizedPromotions.map(p => Number(p.id));

        //Build payload
        const payload = {
          priorityList: this.priorityOrder
        };

        //Call API to change the promotion priority
        const response = await promotionPriority(this.token, payload);
        if (response.data.status === true) {
          this.toast.success(response.data.message);
          this.$router.push('/promotions');
        } else {
          this.toast.error(response.data.message);
        }
      } catch (e) {
        this.toast.error("Failed to print promotions order.");
      } finally {
        this.loading2 = false;
      }
    },

    //Reset Form
    resetForm() {
      this.fetchPromotionsList();
    },
  }
};
</script>

<style scoped>
/* Ensure the body and html take the full height of the page */
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0; /* Background color for the page */
}

/* Container for the entire section */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Center items vertically */
  height: 100%; /* Take full height of the page */
  width: 100%; /* Take full width of the page */
  box-sizing: border-box; /* Ensure padding doesn't affect width */
}

/* Centered Loading Component */
.loading-container {
  display: flex;
  flex-direction: column; /* separate rows */
  justify-content: center;
  align-items: center;
  height: 300px; /* Adjust height to fit modal body if needed */
  font-size: 16px;
  color: #007bff;
  gap: 10px; /* spacing between spinner and text */
}

/* Promotion Priority WorkSpace */
.promo-section {
  width: 100%;
}

/* Card Styles */
.promo-card {
  display: flex;
  align-items: center;
  background: #f5f5f5;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  cursor: grab;
  transition: background 0.2s;
}

/* Card Styles - Hover */
.promo-card:hover {
  background: #e2e6ea;
}

/* Priority Order */
.priority-order {
  width: 40px;
  height: 40px;
  background: black;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0; /* Prevent shrinking */
  margin-right: 16px; /* Space between circle and promo info */
}

/* Priority Order - Number */
.priority-order span {
  font-weight: bold;
  color: white;
}

/* Promotion Info */
.promo-info {
  flex: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Promo Code */
.promo-code {
  font-weight: bold;
}

/* Promo Details */
.promo-details {
  font-size: 0.9rem;
  color: #555;
  margin-top: 2px; /* Optional small spacing between rows */
}

/* Promotion Priority Change Drag Handle */
.drag-handle {
  font-size: 20px;
  cursor: grab;
  margin-right: 10px;
}

/* Promotion Priority Change Drag Handle - Color */
.drag-handle i {
  color: grey;
}

/* Promotion Creation Form - Form Actions (Save/Cancel */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Promotion Creation Form - Form Actions Buttons */
.form-actions button {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
}

/* Promotion Creation Form - Form Actions Button - Cancel */
.form-actions button:first-child {
  background-color: #ccc;
}

/* Promotion Creation Form - Form Actions Button - Save */
.form-actions button:last-child {
  background-color: #0d6efd;
  color: #fff;
}

/* Circular Progress Indicator Styles */
.spinner {
  border: 3px solid #f3f3f3; /* Light gray */
  border-top: 3px solid #3498db; /* Blue */
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

/* Circular Progress Indicator Styles - Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Promotion Not Found Widget */
.not-found-widget {
  max-width: 500px;
  height: 100%;
  display: flex;
  flex-direction: column; /* Stack image and text */
  justify-content: center; /* Vertical center */
  align-items: center; /* Horizontal center */
  margin: 0 auto; /* Center container itself if needed */
  text-align: center; /* Ensure text is centered */
}

/* Promotion Not Found Widget - Image */
.not-found-widget img {
  width: 250px;
  margin-bottom: 15px; /* spacing between image and text */
}

/* Promotion Not Found Widget - Message */
.not-found-widget p {
  color: grey;
  font-size: 18px;
  font-weight: bold;
  margin-top: -30px;
}

/* Priority Groups styling */
.group-title {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 5px;
  color: #333;
}

.group-desc {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 15px;
}

/* Non-draggable card styling */
.promo-card.non-draggable {
  cursor: default;
}

.promo-card.non-draggable:hover {
  background: #f5f5f5;
}

/* Priority Order - Fixed */
.priority-order.fixed-priority {
  background: #888;
}
</style>